import React, { ReactNode, useState, useEffect, useMemo, useCallback } from "react";
import {
  Menu,
  TextInput,
  Box,
  Text,
  Highlight,
  Flex,
  Group,
  Tooltip,
  ActionIcon,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconSearch, IconChevronRight, IconChevronDown } from "@tabler/icons";
import { DomainHeaderIcon, ProductAreaHeaderIcon, SparklesIcon } from "../../icons/x-symbol-svgrepo-com";
import {useKnowledgeData} from "../../hooks/useKnowledgeData";
import {useFields} from "../../hooks/useFields";

interface Item {
  _id?: string;
  id?: string;
  useId?: boolean;
  name: string;
  ownerName?: string;
  domain?: string;
  childComponents?: Item[];
  productComponents?: any[];
  element_path?: string;
}

interface BagelAccordionMenuProps {
  selectedComponent: any;
  children: ReactNode;
  isAiSuggestion?: boolean;
  disabled?: boolean;
  onSelect: (item: Item, type: string) => void;
  placeholder?: string;
  searchAccessor?: string;
  loading?: any;
  /** When true, tooltip shows the assign-product-area prompt (e.g. periodic reminder over ML suggestion). */
  forcePleaseAssignProductAreaTooltip?: boolean;
}

// Builds the domain/component hierarchy path shown in the product area tooltip.
export function buildComponentHierarchyPath(componentObj: any | undefined, components: any[]) {
  if (!componentObj || !componentObj._id) {
    return <Text fz={11.4} color="dimmed">Unassigned</Text>;
  }

  // If component has element_path property, use that
  if (componentObj.element_path) {
    const pathParts = componentObj.element_path.split('.');
    const path: React.ReactNode[] = [];

    // Build path components from the element_path
    pathParts.forEach((part: string, index: number) => {
      if (index === 0) {
        // Domain
        path.push(

          <Flex key={`domain-${index}`} align="center" gap={4}>
            <DomainHeaderIcon fill="#fff" />
            <Text >{part}</Text>
          </Flex>


        );
      } else {
        // Product area or child component
        path.push(

          <Flex key={`component-${index}`} align="center" gap={4}>
            <ProductAreaHeaderIcon fill="#fff" stroke={0.3} />
            <Text >{part}</Text>
          </Flex>

        );
      }
    });

    // Format the path with separators
    return (
      <Group spacing={4} style={{ alignItems: 'center' }}>
        {path.map((element, index) => (
          <React.Fragment key={index}>
            {element}
            {index < path.length - 1 && (
              <Text fz={12} color="dimmed" px={4}>/</Text>
            )}
          </React.Fragment>
        ))}
      </Group>
    );
  }

  // Fallback for components without element_path
  const path: React.ReactNode[] = [];

  // Add domain
  path.push(

    <Flex key="domain" align="center" gap={4}>
      <DomainHeaderIcon fill="#FFF" />
      <Text fz={11.4}>{componentObj.domain || 'Unassigned'}</Text>
    </Flex>

  );

  // Add parent component if exists
  if (componentObj.parentId) {
    const parentComp = components.find((comp: any) => comp._id === componentObj.parentId);
    if (parentComp) {
      path.push(

        <Flex key="parent" align="center" gap={4}>
          <ProductAreaHeaderIcon fill="#FFF" stroke={0.3} />
          <Text fz={11.4}>{parentComp.name}</Text>
        </Flex>

      );
    }
  }

  // Add current component
  path.push(
    <Flex key="current" align="center" gap={4}>
      <ProductAreaHeaderIcon fill="#FFF" stroke={0.3} />
      <Text fz={11.4}>{componentObj?.name}</Text>
    </Flex>
  );

  // Format the path with separators
  return (
    <Group spacing={4} style={{ alignItems: 'center' }}>
      {path.map((element, index) => (
        <React.Fragment key={index}>
          {element}
          {index < path.length - 1 && (
            <Text fz={12} color="dimmed" px={4}>/</Text>
          )}
        </React.Fragment>
      ))}
    </Group>
  );
}

const BagelAccordionMenu: React.FC<BagelAccordionMenuProps> = ({
  selectedComponent,
  onSelect,
  children: target,
  disabled,
  isAiSuggestion = false,
  placeholder = "Select an option",
  searchAccessor = "name",
  loading,
  forcePleaseAssignProductAreaTooltip = false,
}) => {
  // Access domains and components from context
  const { components } = useFields();
  const { domains } = useKnowledgeData();

  // Function to create the label content for display
  const createTooltipLabel = () => {
    // if (isAiSuggestion) {
    //   return (  
    //     <Box p={0.186} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
    //       <Text color="#fff" fw={500}>Suggested by AI</Text>
    //       <SparklesIcon color="#fff" />
    //     </Box>
    //   );
    // }
    
    if (forcePleaseAssignProductAreaTooltip && !disabled) {
      return (
        <Box p={0.186}>
          <Text fz={11.4}>Please assign Product Area</Text>
        </Box>
      );
    }

    if ((!selectedComponent || !selectedComponent._id) && !disabled) {
      return (
        <Box p={0.186}>
          <Text fz={11.4}>Please assign Product Area</Text>
        </Box>
      );
    }
    
    return (
      <Box p={0.186}>
        {isAiSuggestion && <Box mb={8} p={0.186} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Text size={12} color="#fff" fw={500}>Suggested by AI</Text>
          <SparklesIcon size={12} color="#fff" />
        </Box>}
        {buildComponentHierarchyPath(selectedComponent, components)}
      </Box>
    );
  };
  
  // State for search filtering
  const [searchTerm, setSearchTerm] = useState<string>("");
  
  // Menu open/close state
  const [opened, { open, close }] = useDisclosure(false);
  
  // Active submenu tracking
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({});
  
  // Build hierarchical domain tree with element_path for each item
  const hierarchyTree = useMemo(() => {
    // Helper function to build the complete tree with paths
    const buildDomainComponentMap = () => {
      // First get all domains
      const domainMap = domains.map((domain: any) => {
        const domainName = domain.name;
        const domainPath = domainName;
        
        // Find all top-level components belonging to this domain
        const domainComponents = components.filter((comp: any) => 
          comp.domain?.toLowerCase()?.trim() === domain.name?.toLowerCase()?.trim() &&
          !comp.parentId // Only include components without a parent (top-level)
        );
        
        // Process components with their paths
        const processedComponents = domainComponents.map((comp: any) => {
          const componentPath = `${domainPath}.${comp.name}`;
          
          // Process child components to include path
          const childComponentsWithPath = (comp.childComponents || []).map((child: any) => ({
            ...child,
            id: child._id,
            element_path: `${componentPath}.${child.name}`
          }));
          
          return {
            ...comp,
            id: comp._id,
            element_path: componentPath,
            childComponents: childComponentsWithPath
          };
        });

        // Return domain with its components and path
        return {
          ...domain,
          id: domain._id,
          name: domain.name,
          ownerName: domain.ownerName,
          element_path: domainPath,
          productComponents: processedComponents
        };
      });

      // Only include domains that have at least one component
      const filteredDomains = domainMap.filter(domain => 
        domain.productComponents && domain.productComponents.length > 0
      );
      
      return filteredDomains;
    };

    const result = buildDomainComponentMap();
    return result;
  }, [domains, components]);
  
  // Extract domain and product name from selectedComponent
  const selectedDomain = useMemo(() => 
    selectedComponent?.domain || "Unassigned", 
  [selectedComponent]);
  
  const selectedProduct = useMemo(() => 
    selectedComponent?.name || "Unassigned", 
  [selectedComponent]);
  
  // Find the full path to the selected component
  const findProductPath = useCallback(() => {
    if (!selectedComponent || !selectedComponent._id) return null;
    
    // Get the component ID and domain
    const componentId = selectedComponent._id;
    const componentDomain = selectedComponent.domain;
    
    // Helper function to find an item and its path in the hierarchy
    const findComponentInHierarchy = (items: any[], componentId: string): any => {
      // Check if component exists in the current level
      for (const item of items) {
        // Check if this is the component we're looking for
        if (item._id === componentId) {
          return {
            item,
            path: item.element_path
          };
        }
        
        // Check child components if any
        if (item.childComponents && item.childComponents.length > 0) {
          const found = findComponentInHierarchy(item.childComponents, componentId);
          if (found) return found;
        }
        
        // Check product components if domain
        if (item.productComponents && item.productComponents.length > 0) {
          const found = findComponentInHierarchy(item.productComponents, componentId);
          if (found) return found;
        }
      }
      
      return null;
    };
    
    // Find the component and its path in the hierarchy
    const result = findComponentInHierarchy(hierarchyTree, componentId);
    
    if (result) {
      return {
        component: result.item,
        path: result.path
      };
    }
    
    return null;
  }, [hierarchyTree, selectedComponent]);

  // Initialize submenu state based on selected items and AI suggestion
  useEffect(() => {
    // Start with a fresh state when dependencies change
    const newOpenSubmenus: Record<string, boolean> = {};
    
    if (selectedComponent) {
      // Try to find the complete path in the hierarchy
      const pathInfo = findProductPath();
      
      if (pathInfo) {
        // Get all parts of the path
        const pathParts = pathInfo.path.split('.');
        
        // First make sure the domain is open
        const domainName = pathParts[0];
        const domain = hierarchyTree.find(d => d.name === domainName);
        if (domain) {
          newOpenSubmenus[domain.id] = true;
        }
        
        // If we have more than one part, find the product area
        if (pathParts.length > 1) {
          const productName = pathParts[1];
          const domain = hierarchyTree.find(d => d.name === domainName);
          
          if (domain) {
            const product = domain.productComponents.find((p: any) => p.name === productName);
            if (product) {
              newOpenSubmenus[product.id] = true;
            }
          }
        }
        
        // If we have a third part, it's a child component
        if (pathParts.length > 2) {
          const subComponentName = pathParts[2];
          const domain = hierarchyTree.find(d => d.name === domainName);
          
          if (domain) {
            const product = domain.productComponents.find((p: any) => p.name === pathParts[1]);
            if (product && product.childComponents) {
              const childComponent = product.childComponents.find((c: any) => c.name === subComponentName);
              if (childComponent) {
                newOpenSubmenus[childComponent.id] = true;
              }
            }
          }
        }
      } else {
        // Fallback: just open the domain of the selected component
        if (selectedComponent.domain) {
          const domain = hierarchyTree.find(d => 
            d.name?.toLowerCase()?.trim() === selectedComponent.domain?.toLowerCase()?.trim()
          );
          if (domain) {
            newOpenSubmenus[domain.id] = true;
          }
        }
      }
      
      // For AI suggestion, ensure the component itself is open
      if (isAiSuggestion && selectedComponent._id) {
        newOpenSubmenus[selectedComponent._id] = true;
      }
    }
    
    // Only update if we have changes
    if (Object.keys(newOpenSubmenus).length > 0) {
      setOpenSubmenus(newOpenSubmenus);
    }
  }, [selectedComponent, isAiSuggestion, findProductPath, hierarchyTree]);

  // Handle toggling of a submenu
  const toggleSubmenu = useCallback((key: string | number) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  // Reset open submenus when search is cleared
  useEffect(() => {
    if (searchTerm.length <= 1) {
      // Restore open submenus based on selected component
      if (selectedComponent) {
        // Get the domain
        const domain = hierarchyTree.find(d => 
          d.name?.toLowerCase()?.trim() === selectedComponent.domain?.toLowerCase()?.trim()
        );
        
        const newOpenSubmenus: Record<string, boolean> = {};
        
        // Always open the domain
        if (domain) {
          newOpenSubmenus[domain.id] = true;
        }
        
        // Open path to component
        const pathInfo = findProductPath();
        if (pathInfo) {
          pathInfo.path.split('.').forEach((part: string) => {
            newOpenSubmenus[part] = true;
          });
        }
        
        // If has parent, open parent
        if (selectedComponent.parentId) {
          newOpenSubmenus[selectedComponent.parentId] = true;
        }
        
        // For AI suggestion
        if (isAiSuggestion && selectedComponent._id) {
          newOpenSubmenus[selectedComponent._id] = true;
        }
        
        setOpenSubmenus(newOpenSubmenus);
      } else {
        setOpenSubmenus({});
      }
    }
  }, [searchTerm, selectedComponent, findProductPath, hierarchyTree, selectedDomain, isAiSuggestion]);

  // Enhance the search functionality to use element_path
  const filterItems = useCallback((items: Item[], term: string): Item[] => {
    if (!term) return items;
    
    term = term.toLowerCase();
  
    return items
      .map((item: any) => {
        // Check if this item matches the search by name or by path
        const matchesName = item[searchAccessor]?.toLowerCase().includes(term);
        const matchesPath = item.element_path?.toLowerCase().includes(term);
        const matched = matchesName || matchesPath;

        // Recursively filter child components
        const filteredChildComponents = item.childComponents
          ? filterItems(item.childComponents, term)
          : [];
  
        // Recursively filter product components
        const filteredProductComponents = item.productComponents
          ? filterItems(item.productComponents, term)
          : [];
  
        // Item should be included if it matches or has children that match
        const hasNestedMatches =
          filteredChildComponents.length > 0 ||
          filteredProductComponents.length > 0;
  
        if (matched || hasNestedMatches) {
          return {
            ...item,
            ...(filteredChildComponents.length > 0 && { childComponents: filteredChildComponents }),
            ...(filteredProductComponents.length > 0 && { productComponents: filteredProductComponents }),
          };
        }
  
        return null;
      })
      .filter(Boolean) as Item[];
  }, [searchAccessor]);

  // When search term changes and we find matches, open them
  useEffect(() => {
    if (searchTerm && searchTerm.length > 1) {
      const filteredItemsResult = filterItems(hierarchyTree, searchTerm);
      
      // If we have results, prepare to open paths to all matching items
      if (filteredItemsResult.length > 0) {
        const newOpenSubmenus: Record<string, boolean> = {};
        
        // Helper to collect all IDs that should be open
        const collectOpenIds = (items: any[]) => {
          items.forEach(item => {
            // Add this item's ID to the open set
            if (item.id || item._id) {
              newOpenSubmenus[item.id || item._id] = true;
            }
            
            // Check child components
            if (item.childComponents && item.childComponents.length > 0) {
              collectOpenIds(item.childComponents);
            }
            
            // Check product components
            if (item.productComponents && item.productComponents.length > 0) {
              collectOpenIds(item.productComponents);
            }
          });
        };
        
        collectOpenIds(filteredItemsResult);
        
        // Update open submenus to show search results
        setOpenSubmenus(prev => ({
          ...prev,
          ...newOpenSubmenus
        }));
      }
    }
  }, [searchTerm, filterItems, hierarchyTree]);

  // Generate filtered items based on current search term
  const filteredItems = useMemo(() => 
    filterItems(hierarchyTree, searchTerm),
  [hierarchyTree, searchTerm, filterItems]);

  // Handle menu open event - ensure all necessary menus are open
  const handleMenuOpen = useCallback(() => {
    // Make sure all necessary submenus are open when the menu is opened
    if (selectedComponent) {
      const newOpenSubmenus: Record<string, boolean> = {};
      
      // Always open the domain
      const domainObj = hierarchyTree.find(d => 
        d.name?.toLowerCase()?.trim() === selectedComponent.domain?.toLowerCase()?.trim()
      );
      
      if (domainObj) {
        const domainKey = domainObj.id;
        newOpenSubmenus[domainKey] = true;
      }
      
      // Open path to the selected component
      const pathInfo = findProductPath();
      
      if (pathInfo) {
        // Open all items in the path
        pathInfo.path.split('.').forEach((part: string) => {
          newOpenSubmenus[part] = true;
        });
      }
      
      // Parent handling
      if (selectedComponent.parentId) {
        newOpenSubmenus[selectedComponent.parentId] = true;
      }
      
      // AI suggestion
      if (isAiSuggestion) {
        if (selectedComponent._id) {
          newOpenSubmenus[selectedComponent._id] = true;
        }
      }
      
      // Update state with all the required open submenus
      setOpenSubmenus(prev => ({
        ...prev,
        ...newOpenSubmenus
      }));
    }
    
    open();
  }, [open, selectedComponent, findProductPath, hierarchyTree, isAiSuggestion]);

  // Is the current component an AI suggestion?
  const isItemAiSuggested = useCallback((item: any, type: string): boolean => {
    if (!isAiSuggestion || !selectedComponent || !selectedComponent._id) {
      return false;
    }
    
    return type === "product" && item._id === selectedComponent._id;
  }, [isAiSuggestion, selectedComponent]);

  // Enhanced rendering function to correctly display child components
  const renderItems = useCallback((items: Item[], type: string, selected: string): React.ReactNode => {
    return items.map((item, index) => {
      const key = item.id || item._id || item.name || index;
      const isOpen = openSubmenus[key];
      const showArrowOnSearch = openSubmenus[key] === undefined && searchTerm;
      
      // Check if this item has child components
      const hasChildren = !!(item.childComponents && item.childComponents.length > 0);
      
      // Determine if we should show an expandable arrow
      const showArrow = hasChildren || 
                        (type === "Domain" && item.productComponents && item.productComponents.length > 0);
      
      // Check if this item is the AI-suggested product
      const isAiSuggestedItem = isItemAiSuggested(item, type);
      
      // Determine if this item is selected
      const isItemSelected = type === "Domain" 
        ? item.name === selectedDomain
        : item._id === selectedComponent?._id;
      
      return (
        <React.Fragment key={`${type}-${index}-${key}`}>
          <Menu.Item
            p={5}
            my={3}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(item, type);
              
              if (type === "Domain") {
                // When selecting a domain, toggle its submenu
                toggleSubmenu(key);
              } else {
                // For other items, close the menu after selection
                close();
                e.stopPropagation();
              }
            }}
            sx={{
              flex: 1,
              backgroundColor: 
                isAiSuggestedItem 
                  ? "#F5F6FE" // Background for AI suggestions
                  : isItemSelected 
                    ? "#F5F6FE" // Background for selected items
                    : "transparent",
            }}
          >

            <Flex ml={!showArrow ? "xs" : "0px"} align="center" justify="left" direction={"row-reverse"}>
              <Tooltip
                label={item?.name || 'Select'}
                withArrow
                transitionProps={{ duration: 200 }}
                disabled={/* type === "Domain" &&   */item?.name?.length < 10}
                multiline
                variant="light"
                maw={400}
              >
                <Flex align="center" gap="7px"  pl={!showArrow ? 6 : 0}>
                  <Text sx={{minWidth:"12px", minHeight:"14px", display:"flex"}}>
                    {type === "Domain" 
                      ? <DomainHeaderIcon fill='#adb5bd' color='#adb5bd'/> 
                      : <ProductAreaHeaderIcon
                        fill={isAiSuggestedItem ? '#5C5CEB' : '#adb5bd'} 
                        color={isAiSuggestedItem ? '#5C5CEB' : '#adb5bd'} 
                      />
                    }
                  </Text>
                  <Highlight 
                    highlight={searchTerm}
                    sx={{
                      color: isAiSuggestedItem ? "#5C5CEB" : "inherit",
                      fontWeight: isAiSuggestedItem ? 500 : 'inherit'
                    }}
                    lineClamp={1}
                  >
                    {item.name}
                  </Highlight>
                  {isAiSuggestedItem && (
                    <Box mb={-3}>
                      <SparklesIcon color={"#5B60E3"} />
                    </Box>
                  )}
                </Flex>
              </Tooltip>
              {showArrow && (
                <ActionIcon
                  size="xs"
                  mr={5}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Toggle submenu when clicking the arrow
                    toggleSubmenu(key);
                  }}
                  sx={{
                    '&:hover svg': {
                      color: 'black',
                      stroke: 'black',
                    }
                  }}
                >
                  {isOpen || showArrowOnSearch ? 
                    <IconChevronDown size={14} color="#adb5bd" /> : 
                    <IconChevronRight size={14} color="#adb5bd" />
                  }
                </ActionIcon>
              )}
            </Flex>
          </Menu.Item>

          {/* If this item has child components and is open, render them */}
          {isOpen && hasChildren && (
            <Box pl="md">
              {renderItems(item.childComponents || [], "product", selected)}
            </Box>
          )}

          {/* If this is a Domain and has product components and is open, render them */}
          {isOpen && type === "Domain" && item.productComponents && item.productComponents.length > 0 && (
            <Box pl="md">
              {renderItems(item.productComponents || [], "product", selectedProduct)}
            </Box>
          )}
        </React.Fragment>
      );
    });
  }, [openSubmenus, searchTerm, isAiSuggestion, selectedDomain, selectedProduct, 
    selectedComponent, onSelect, close, toggleSubmenu, isItemAiSuggested]);

  return (
    <Menu
      shadow="md"
      closeOnItemClick={false}
      opened={opened}
      onOpen={handleMenuOpen}
      onClose={close}
    >
      <Menu.Target>
        <Tooltip 
          label={createTooltipLabel()}
          withArrow
          withinPortal
          transitionProps={{ duration: 200 }}
        >
          {target}
        </Tooltip>
      </Menu.Target>
      {!disabled && (
        <Menu.Dropdown 
          maw={250}
          style={{
            position: 'absolute', 
            zIndex: 300
          }}
        >
          <TextInput
            icon={<IconSearch size={14} />}
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
          />
          <Box 
            sx={{ 
              overflowY: "auto",
              willChange: 'transform, opacity',
              maxHeight: 400,
              minHeight: filteredItems.length === 0 ? 50 : undefined
            }} 
            // pt="xs"
          >
            {filteredItems.length === 0 ? (
              <Text p="sm" color="dimmed" align="center" size="xs">
                No matches for "{searchTerm}"
              </Text>
            ) : (
              renderItems(filteredItems, "Domain", selectedDomain)
            )}
          </Box>
        </Menu.Dropdown>
      )}
    </Menu>
  );
};

export default BagelAccordionMenu;
